import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { HardDrive, Download, RefreshCw, Mail, Calendar, Loader2, PlayCircle } from 'lucide-react';
import api from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BackupsPage({ user, onLogout }) {
  const [items, setItems] = useState([]);
  const [emailTo, setEmailTo] = useState('');
  const [schedule, setSchedule] = useState('');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/backups');
      setItems(res.data.backups || []);
      setEmailTo(res.data.email_to || '');
      setSchedule(res.data.schedule_ist || '02:00');
    } catch (err) {
      toast.error('Could not load backups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRunNow = async () => {
    if (running) return;
    setRunning(true);
    setLastResult(null);
    const t = toast.loading('Running backup… mongodump + JSON export + email');
    try {
      const res = await api.post('/admin/backups/run-now');
      setLastResult(res.data);
      toast.dismiss(t);
      if (res.data.status === 'success') {
        toast.success(`Backup complete · ${res.data.archive_size_mb} MB · emailed to ${res.data.email?.sent_to}`);
      } else {
        toast.error(`Backup failed: ${res.data.error || 'unknown'}`);
      }
      await load();
    } catch (err) {
      toast.dismiss(t);
      toast.error(err.response?.data?.detail || 'Backup failed');
    } finally {
      setRunning(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${BACKEND_URL}/api/admin/backups/download/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Could not download backup');
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <header>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <HardDrive className="w-6 h-6 text-slate-700" /> Database Backups
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Daily backup of every MongoDB collection (mongodump BSON + JSON export), bundled and emailed automatically.
          </p>
        </header>

        {/* Status row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="p-4 rounded-2xl border-emerald-200 bg-emerald-50/40">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Schedule</p>
                <p className="font-semibold text-slate-900" data-testid="backups-schedule">Daily at {schedule} IST</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 rounded-2xl border-blue-200 bg-blue-50/40">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Emailed to</p>
                <p className="font-semibold text-slate-900 break-all" data-testid="backups-email-to">{emailTo || '—'}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 rounded-2xl border-purple-200 bg-purple-50/40">
            <div className="flex items-center gap-3">
              <HardDrive className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Retention</p>
                <p className="font-semibold text-slate-900">Last 30 days + 1st of every month forever</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Run now */}
        <Card className="p-5 rounded-2xl border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2"><PlayCircle className="w-5 h-5 text-indigo-600" /> Run a backup now</h3>
            <p className="text-sm text-slate-500">Creates a fresh backup, emails it, and applies retention. Typically takes a few seconds.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={loading} className="rounded-xl" data-testid="backups-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button onClick={handleRunNow} disabled={running} className="bg-indigo-600 hover:bg-indigo-700 rounded-xl" data-testid="backups-run-now-btn">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlayCircle className="w-4 h-4 mr-2" /> Run backup now</>}
            </Button>
          </div>
        </Card>

        {/* Last result */}
        {lastResult && (
          <Card className={`p-4 rounded-2xl border ${lastResult.status === 'success' ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`} data-testid="backups-last-result">
            <p className="font-semibold text-slate-900 mb-1">
              Last run: {lastResult.status === 'success' ? '✅ Success' : '❌ Failed'}
            </p>
            <ul className="text-sm text-slate-700 space-y-0.5">
              <li>Collections: {lastResult.collection_count}</li>
              <li>Archive: {lastResult.archive} ({lastResult.archive_size_mb} MB)</li>
              <li>Email: {lastResult.email?.sent_to ? `sent to ${lastResult.email.sent_to}${lastResult.email.attached ? ' (with attachment)' : ' (link only — file too large)'}` : (lastResult.email?.error || '—')}</li>
              {lastResult.retention && (
                <li>Retention: kept {lastResult.retention.kept}, deleted {lastResult.retention.deleted}</li>
              )}
              {lastResult.error && <li className="text-rose-700">Error: {lastResult.error}</li>}
            </ul>
          </Card>
        )}

        {/* List */}
        <Card className="rounded-2xl border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Stored backups</h3>
            <Badge variant="outline">{items.length}</Badge>
          </div>
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-slate-400">No backups yet. Click <strong>Run backup now</strong> to create the first one.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">Filename</th>
                  <th className="px-4 py-3 text-left">Size</th>
                  <th className="px-4 py-3 text-left">Created</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it) => (
                  <tr key={it.filename} className="hover:bg-slate-50" data-testid={`backup-row-${it.filename}`}>
                    <td className="px-4 py-3 font-mono text-slate-800">{it.filename}</td>
                    <td className="px-4 py-3 text-slate-600">{it.size_mb} MB</td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(it.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" className="rounded-xl" onClick={() => handleDownload(it.filename)} data-testid={`backup-download-${it.filename}`}>
                        <Download className="w-4 h-4 mr-1" /> Download
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card className="p-4 rounded-2xl bg-slate-50 border-slate-200 text-sm text-slate-700">
          <p className="font-semibold mb-1">How to restore from a backup</p>
          <ol className="list-decimal pl-5 space-y-0.5">
            <li>Download the <code className="bg-slate-200 px-1 rounded">.tar.gz</code>.</li>
            <li>Extract: <code className="bg-slate-200 px-1 rounded">tar -xzf eden_backup_YYYY-MM-DD.tar.gz</code>.</li>
            <li>Inside, use the <code className="bg-slate-200 px-1 rounded">mongodump/</code> folder with <code className="bg-slate-200 px-1 rounded">mongorestore</code>, or open the <code className="bg-slate-200 px-1 rounded">json/</code> folder to inspect any collection.</li>
          </ol>
        </Card>
      </div>
    </Layout>
  );
}
